import { SetLoading } from '@arema/components/Classes';
import { bindClick, fromUnix, getNowLocal } from '@arema/components/Util';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Header, Icon, Label, Message, Table, Modal, useFrontier } from 'react-frontier2';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { firstBy } from 'thenby';
import { useUser } from '../AdminHooks';
import API from '../API';
import UserAccess from '../UserAccess';

interface TicketPrintsModalProps{
	ticketId: number,
	ticketHash?: string,
	open: boolean,
	onClose: ()=>void,
}

enum PrintCancelReason{
	GENERIC = 1,
	REPRINT = 2,
	MANUAL = 3,
}

interface TicketPrint{
	print_id: number,
	ticket_id: number,
	pdv_id: number,
	pdv_name: string,
	date_created: number,
	cancelled: boolean,
	cancel_reason: PrintCancelReason,
	user_creator: number,
	user_creator_name: string,
	date_cancelled?: number,
	user_cancelled?: number,
	user_cancelled_name?: string,
}

var TicketPrintsModal = (props: TicketPrintsModalProps)=>{
	var { t } = useTranslation();
	var { hasAccess, user } = useUser();
	var { Toast } = useFrontier();
	var [prints, setPrints] = useState<TicketPrint[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [cancelling, setCancelling] = useState<number>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null)

	var access = useMemo(()=>({
		CANCEL_PRINT: hasAccess(UserAccess.ORDERS.CANCEL_TICKET_PRINT),
	}), []);

	useEffect(()=>{
		if(!props.ticketId || !props.open) return;
		setPrints(null);
		setLoadError(null);
		API.getTicketPrints(props.ticketId).then(res=>{
			if(res.error) return setLoadError(res.message);
			setPrints(res.data);
		}).catch(err=>{
			return setLoadError(t('common.unexpected_code', { code: 'LCL-TKPRNTMD-1' }));
		})
	}, [props.ticketId, props.open]);

	var cancelling_print = useMemo(()=>{
		if(!cancelling || !prints) return null;
		return prints.find(a=>a.print_id===cancelling);
	}, [cancelling, prints]);

	var showCancel = (print_id: number)=>{
		return ()=>{
			setCancelling(print_id);
			setModalPrompts(null);
		}
	}

	var sendCancel = (setLoading: SetLoading)=>{
		if(!cancelling) return;
		setLoading(true);
		setModalPrompts(null);
		API.cancelTicketPrint(cancelling).then(res=>{
			if(res.message) return setModalPrompts([res.message]);
			var pr = [...prints];
			var px = pr.findIndex(a=>a.print_id===cancelling);
			if(px!==-1){
				pr[px].cancelled = true;
				pr[px].cancel_reason = PrintCancelReason.MANUAL;
				pr[px].date_cancelled = getNowLocal().toSeconds();
				pr[px].user_cancelled = user.user_id;
				pr[px].user_cancelled_name = user.name;
				setPrints(pr);
			}
			setCancelling(null);
			Toast.success(t('ticket_prints.print_cancelled'));
		}).catch(err=>{
			return setModalPrompts([t('common.unexpected_code', { code: 'LCL-TKPRNTCL-1' })]);
		}).finally(()=>{
			setLoading(false);
		});
	}
	
	return <>
		<Modal open={props.open} onClose={props.onClose}>
			<Modal.Header>{t('ticket_prints.title', { code: props.ticketHash || `TK${props.ticketId}` })}</Modal.Header>
			<Modal.Content>
				{(!prints || !!loadError) ? (
					<Header loading={!loadError} text={!!loadError ? t('common.error') : t('common.loading')} subtext={loadError} />
				) : (
					<Table fitted style={{ marginTop: -20 }}>
						<Table.Head
							data={[['ID', 'Fecha', 'Usuario', 'PDV', 'Estatus', <Icon name='times' style={{ margin: 0 }} />]]}
							collapsingIndexes={[0, 1, 4, 5]}
						/>
						<Table.Body>
							{prints.length===0 && (
								<Table.Cell row empty colSpan={100} value={t('ticket_prints.empty')} />
							)}
							{prints.sort(firstBy('cancelled', 'asc').thenBy('date_created', 'desc')).map(a=>(
								<React.Fragment key={`PRTRW-${a.print_id}`}>
									<Table.Row collapsingIndexes={[0, 1, 2, 4]} centeredIndexes={[0]} data={[
										a.print_id,
										fromUnix(a.date_created).toFormat('dd/MM/yy HH:mm:ss'),
										<Link to={`/users/${a.user_creator}`} target={'_blank'} className='normal'>{a.user_creator_name}</Link>,
										a.pdv_id ? (
											<Link to={`/pdv/${a.pdv_id}`} target={'_blank'} className='normal'>{a.pdv_name}</Link>
										) : (
											<Icon name='minus' color='gray' />
										),
										<Label color={a.cancelled ? 'red' : 'green'} style={{ minWidth: 110 }}>
											{a.cancelled ? t('common.cancelled') : t('common.active')}
										</Label>,
										<Button size='tiny' icon iconName='times' onClick={showCancel(a.print_id)} disabled={!access.CANCEL_PRINT || !!a.cancelled} />
									]} />
									{!!a.cancelled && (
										<Table.Row style={{ backgroundColor: '#f0f0f0', color: '#888' }}>
											<Table.Cell centered collapsing>
												<Icon name='arrow-turn-up' style={{ transform: 'scaleX(-1)', marginRight: 0 }} />
											</Table.Cell>
											<Table.Cell collapsing>
												{fromUnix(a.date_cancelled).toFormat('dd/MM/yy HH:mm:ss')}
											</Table.Cell>,
											<Table.Cell>
												{a.user_cancelled ? (
													<Link to={`/users/${a.user_cancelled}`} target={'_blank'} className='normal'>{a.user_cancelled_name}</Link>
												) : (
													<Icon name='minus' color='gray' />
												)}
											</Table.Cell>
											<Table.Cell colSpan={3}>
												{t('ticket_prints.reason', { code: t(`print_cancel_reason.reason_${a.cancel_reason}`) })}
											</Table.Cell>
										</Table.Row>
									)}
								</React.Fragment>
							))}
						</Table.Body>
					</Table>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} onClick={props.onClose} />
			</Modal.Actions>
		</Modal>
		<Modal open={!!cancelling_print} onClose={bindClick(setCancelling)} size={'mini'}>
			<Modal.Header>{t('ticket_prints.cancel_print', { code: cancelling })}</Modal.Header>
			{!!cancelling_print && <Modal.Content>
				<Table details>
					<Table.Head title={t('common.info')} />
					<Table.Body
						data={[
							['ID', cancelling_print.print_id],
							['Fecha', fromUnix(cancelling_print.date_created).toFormat('dd/MMM/yy HH:mm:ss')],
							['Usuario', <Link className='normal' to={`/users/${cancelling_print.user_creator}`} target={'_blank'}>{cancelling_print.user_creator_name}</Link>],
							['PDV', <Link className='normal' to={`/users/${cancelling_print.pdv_id}`} target={'_blank'}>{cancelling_print.pdv_name}</Link>],
						]}
					/>
				</Table>
				<Message type='info' style={{ marginTop: 15 }}>
					{t('ticket_prints.cancel_message')}
				</Message>
				<Message type='error' list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text={t('common.close')} basic onClick={bindClick(setCancelling)} />
				<Button text={t('common.cancel')} color={'red'} onClick={sendCancel} />
			</Modal.Actions>
		</Modal>
	</>
}

export default TicketPrintsModal;